'use client';

import { createContext, useContext, ReactNode, useState } from 'react';
import { useTicketInfo, useQuickBet, AcceptTicketStatus, useBBPlusTicketInfo } from '@/hooks/query';
import { useBetslipStore } from '@/store/betslip';

interface BettingContextValue {
	quickBetStatus: AcceptTicketStatus | null;
	quickBetTicketId: string | undefined;
	isQuickBetLoading: boolean;
	// useTicketInfo results
	refetchTicket: () => void;
	isFetchingTickets: boolean;
	syncTicketResponse: unknown;
	ticketInfoError: unknown;
	ticketInfoIsError: boolean;
	// useBBPlusTicketInfo results
	refetchBBPlusTicket: () => void;
	isFetchingBBPlusTickets: boolean;
	syncBBPlusTicketResponse: unknown;
	bbPlusTicketInfoError: unknown;
	bbPlusTicketInfoIsError: boolean;
	oddsKey: string;
	setOddsKey: (oddsKey: string) => void;
}

const BettingContext = createContext<BettingContextValue | undefined>(undefined);

export const useBettingContext = () => {
	const context = useContext(BettingContext);
	if (!context) {
		throw new Error('useBettingContext must be used within BettingProvider');
	}
	return context;
};


interface BettingProviderProps {
	children: ReactNode;
}

// 此provider負責集中管理 關於 betting 的相關useQuery 之前將useQuery分散在各個Odds Button 會造成效能問題
export const BettingProvider = ({ children }: BettingProviderProps) => {
	const [
		currentMultiTickets, 
		currentBBPlusTickets
	] = useBetslipStore(state => [
		state.ticketCarts['normal'].multiTickets, 
		state.ticketCarts['bb-plus'].multiTickets
	]);
	const [oddsKey, setOddsKey] = useState<string>('');
	const { 
		refetch: refetchTicket, 
		isLoading: isFetchingTickets, 
		syncTicketResponse, 
		error: ticketInfoError, 
		isError: ticketInfoIsError 
	} = useTicketInfo(currentMultiTickets);

	const {
		refetch: refetchBBPlusTicket,
		isLoading: isFetchingBBPlusTickets,
		syncBBPlusTicketResponse,
		error: bbPlusTicketInfoError,
		isError: bbPlusTicketInfoIsError,
	} = useBBPlusTicketInfo(currentBBPlusTickets);

	const { 
		status: quickBetStatus, 
		isLoading: isQuickBetLoading, 
		ticketId: quickBetTicketId 
	} = useQuickBet(oddsKey, setOddsKey);

	const value: BettingContextValue = {
		refetchTicket,
		isFetchingTickets,
		syncTicketResponse,
		ticketInfoError,
		ticketInfoIsError,
		refetchBBPlusTicket,
		isFetchingBBPlusTickets,
		syncBBPlusTicketResponse,
		bbPlusTicketInfoError,
		bbPlusTicketInfoIsError,
		quickBetStatus,
		quickBetTicketId,
		isQuickBetLoading,
		oddsKey,
		setOddsKey,
	};

	return (
		<BettingContext.Provider value={value}>
			{children}
		</BettingContext.Provider>
	);
};

